import React, { useState, useEffect } from "react"
import { toast } from 'react-toastify';

const SocialMediaDashboardPage = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [postAnalytics, setPostAnalytics] = useState({
    likes: 0,
    comments: 0,
    shares: 0,
    lastUpdated: null
  });
  const [dashboardStats, setDashboardStats] = useState({
    totalPosts: 0,
    published: 0,
    scheduled: 0,
    pages: 0
  });


  const getPlatformIcon = (platform) => {
      if (platform === 'facebook') {
        return (
          <img 
          src="/assets/icons/facebook-icon.png" 
          alt="Facebook Icon" 
          width="16" 
          height="16" 
          />
        )
      } else if (platform === 'instagram') {
        return (
          <img 
          src="/assets/icons/insta-icon.png" 
          alt="Instagram Icon" 
          width="16" 
          height="16" 
          />
        )
      }
    }


  // Get Facebook data from DOM element (set by Rails controller)
  const getFacebookDataFromDOM = () => {
    const reactRoot = document.getElementById('react-root');
    const facebookConnected = reactRoot?.getAttribute('facebook-connected') === 'true';
    const facebookPagesJson = reactRoot?.getAttribute('facebook-pages') || '[]';
    let facebookPages = [];
    
    try {
      facebookPages = JSON.parse(facebookPagesJson);
    } catch (error) {
      console.error('Error parsing Facebook pages data:', error);
      facebookPages = [];
    }
    
    return {
      connected: facebookConnected,
      pages: facebookPages
    };
  };

  // Use Facebook data from DOM
  const facebookData = getFacebookDataFromDOM();
  const facebookConnected = facebookData.connected;


  const handleRefreshAnalytics = async () => {
    try {
      setIsRefreshing(true);
      toast.info('Refreshing analytics...');
      
      const response = await fetch('/api/facebook/posts/refresh_analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPostAnalytics({
          likes: data.analytics.likes || 0,
          comments: data.analytics.comments || 0,
          shares: data.analytics.shares || 0,
          lastUpdated: new Date().toLocaleString()
        });
        toast.success('Analytics refreshed successfully!');
        console.log('Analytics updated:', data.analytics);
      } else {
        toast.error(data.error || 'Failed to refresh analytics');
      }
    } catch (error) {
      console.error('Error refreshing analytics:', error);
      toast.error('Failed to refresh analytics. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePageToggle = async (pageId) => {
    try {
      const response = await fetch(`/api/facebook/pages/${pageId}/toggle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Page ${data.page.active ? 'enabled' : 'disabled'} successfully!`);
        // Reload the page to show updated status
        window.location.reload();
      } else {
        toast.error(data.error || 'Failed to toggle page');
      }
    } catch (error) {
      console.error('Error toggling page:', error);
      toast.error('Failed to toggle page. Please try again.');
    }
  };

  const handleDeletePage = async (pageId, pageName) => {
    if (!window.confirm(`Are you sure you want to delete the page "${pageName}"? This will also delete all associated posts.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/facebook/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        // Reload the page to show updated pages
        window.location.reload();
      } else {
        toast.error(data.error || 'Failed to delete page');
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page. Please try again.');
    }
  };

  const openPopup = (url) => {
    const width = 600;
    const height = 700;
    const popupLeft = (window.innerWidth - width) / 2;
    const popupTop = (window.innerHeight - height) / 2;

    const popup = window.open(
      url,
      `fbAuth_${Date.now()}`,
      `popup=yes,width=${width},height=${height},top=${popupTop},left=${popupLeft},resizable=yes,scrollbars=yes,toolbar=0,location=0,menubar=0,status=0`
    );

    if (!popup) {
      alert('Please allow popups in your browser to connect Facebook. Redirecting...');
      window.location.href = url;
      return;
    }

    const timer = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(timer);
        window.location.reload();
      }
    }, 1000);
  };

  const handleValidatePage = async (pageId) => {
    try {
      toast.info('Validating page access...');
      
      const response = await fetch(`/api/facebook/pages/${pageId}/validate`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (data.valid) {
          toast.success(data.message);
        } else {
          toast.warning(data.message);
          // Reload the page to show updated pages
          window.location.reload();
        }
      } else {
        toast.error(data.error || 'Failed to validate page');
      }
    } catch (error) {
      console.error('Error validating page:', error);
      toast.error('Failed to validate page. Please try again.');
    }
  };

  const handleAddAnotherPage = () => {
    // Open Facebook OAuth in a popup to add more pages
    openPopup('/facebook/oauth/authorize?popup=1');
  };

  const handleRefreshPages = async () => {
    if (isRefreshing) return; // Prevent multiple clicks
    
    setIsRefreshing(true);
    try {
      const response = await fetch('/api/facebook/refresh_pages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        // Reload the page to show updated pages
        window.location.reload();
      } else {
        toast.error(data.error || 'Failed to refresh pages');
      }
    } catch (error) {
      console.error('Error refreshing pages:', error);
      toast.error('Failed to refresh pages');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="marketing__page">
      <div className="marketing__content">
        {/* Dashboard Content */}
        {facebookConnected && (
          <div className="dashboard-content">
                  {/* Impact Summary Section */}
                  <div className="impact-summary-page-card">
                    <div>
                      <h2 className="impact-title">Impact Summary</h2>
                      <p className="impact-subtitle">Last 30 days</p>
                    </div>
                  </div>

                  {/* Connected Pages Section */}
                  <div className="connected-pages-section">
                    <div className="connected-pages-header">
                      <h3 className="connected-pages-title">Connected Pages</h3>
                      <button 
                        className={`btn btn-outline-primary btn-sm ${isRefreshing ? 'loading' : ''}`}
                        onClick={handleRefreshPages}
                        disabled={isRefreshing}
                      >
                        {isRefreshing ? 'Refreshing...' : 'Refresh Pages'}
                      </button>
                    </div>
                    <div className="pages-grid">
                      {/* Existing Facebook Pages */}
                      {facebookData.pages && facebookData.pages.length > 0 && 
                        facebookData.pages.map((page) => (
                          <div key={page.id} className="page-card">
                            <div className="page-card-header">
                              <div className="page-icon">
                                <i className="fab fa-facebook-f text-white"></i>
                              </div>
                              <div className="page-info">
                                <h4>{page.page_name}</h4>
                                <span className={`page-status ${page.active ? 'active' : 'inactive'}`}>
                                  {page.active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <div className="page-card-actions">
                            </div>
                          </div>
                        ))
                      }
                      
                      {/* Add Another Page Card */}
                      <div className="add-page-card" onClick={handleAddAnotherPage}>
                        <div className="add-page-icon">+</div>
                        <div className="add-page-text">Add Another Page</div>
                      </div>
                    </div>
                  </div>

                  {/* Stats Cards */}
                  <div className="stats-cards-container">
                    <div className="stat-card">
                      <div className="stat-content">
                        <div className="stat-label">Total Posts</div>
                        <div className="stat-number">{dashboardStats.totalPosts}</div>
                      </div>
                      <div className="stat-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 3v16a2 2 0 0 0 2 2h16"></path>
                          <path d="M18 17V9"></path>
                          <path d="M13 17V5"></path>
                          <path d="M8 17v-3"></path>
                        </svg>
                      </div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-content">
                        <div className="stat-label">Scheduled</div>
                        <div className="stat-number">{dashboardStats.scheduled}</div>
                      </div>
                      <div className="stat-icon scheduled">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-content">
                        <div className="stat-label">Published</div>
                        <div className="stat-number">{dashboardStats.published}</div>
                      </div>
                      <div className="stat-icon published">
                        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" role="img" viewBox="0 0 24 24" className="w-5 h-5 text-blue-600" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"></path>
                        </svg>
                      </div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-content">
                        <div className="stat-label">Pages</div>
                        <div className="stat-number">{dashboardStats.pages}</div>
                      </div>
                      <div className="stat-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cards */}
                  <div className="analytics-section">
                    <div className="analytics-header">
                      <h3 className="analytics-title">Post Analytics</h3>
                      <button 
                        className={`btn btn-outline-primary btn-sm ${isRefreshing ? 'loading' : ''}`}
                        onClick={handleRefreshAnalytics}
                        disabled={isRefreshing}
                      >
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </div>
                    <div className="analytics-cards">
                      <div className="analytics-card">
                        <div className="analytics-icon likes">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 10v12"></path>
                            <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path>
                          </svg>
                        </div>
                        <div className="analytics-content">
                          <div className="analytics-number">{postAnalytics.likes}</div>
                          <div className="analytics-label">Likes</div>
                        </div>
                      </div>
                      
                      <div className="analytics-card">
                        <div className="analytics-icon comments">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                          </svg>
                        </div>
                        <div className="analytics-content">
                          <div className="analytics-number">{postAnalytics.comments}</div>
                          <div className="analytics-label">Comments</div>
                        </div>
                      </div>
                      
                      <div className="analytics-card">
                        <div className="analytics-icon shares">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                            <polyline points="16,6 12,2 8,6"></polyline>
                            <line x1="12" y1="2" x2="12" y2="15"></line>
                          </svg>
                        </div>
                        <div className="analytics-content">
                          <div className="analytics-number">{postAnalytics.shares}</div>
                          <div className="analytics-label">Shares</div>
                        </div>
                      </div>
                    </div>
                    {postAnalytics.lastUpdated && (
                      <div className="analytics-footer">
                        <small className="text-muted">Last updated: {postAnalytics.lastUpdated}</small>
                      </div>
                    )}
                  </div>
                </div>
            )}

        {/* No Facebook Connection Message */}
        {!facebookConnected && (
          <div className="no-connection-section text-center p-5">
            <div className="mb-4">
              <i className="fas fa-link fa-3x text-muted"></i>
            </div>
            <h4>Connect Facebook to Get Started</h4>
            <p className="text-muted mb-4">
              Connect your Facebook account to start managing your social media content and analytics.
            </p>
            <button className="btn btn-primary btn-lg" onClick={() => openPopup('/facebook/oauth/authorize?popup=1')}>
              Connect Facebook Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMediaDashboardPage;
